import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import PayrollRunDto from '@/models/Payroll/PayrollRunDto';
import TimesheetDto from '@/models/Timesheets/TimesheetDto';

// Mirrors api/core/payroll.go::PayrollRunWorkerSummary.
// Per-step state (workerSyncedAt / payslipFetchedAt / holidayFetchedAt) comes
// from the payroll_run_worker projection; bound/sent counts come from the
// timesheet table. The matching *Error fields hold the latest failure reason
// for each step so the Workers tab can surface what went wrong without
// trawling the run-wide error log.
export interface PayrollRunWorkerSummary {
    workerId: string;
    firstName: string;
    lastName: string;
    externalPayrollSystemId: string;
    boundTimesheetCount: number;
    sentTimesheetCount: number;
    workerSyncedAt: string | null;
    workerSyncError: string;
    payslipFetchedAt: string | null;
    payslipFetchError: string;
    holidayFetchedAt: string | null;
    holidayFetchError: string;
}

// Mirrors api/core/payroll.go::PayrollRunLocationSummary. Drives the
// Locations tab: NEW = pending customer push to upstream payroll system,
// EXISTING = already synced (externalPayrollSystemId set). Customer sync
// error is the last failure reason for that location.
export interface PayrollRunLocationSummary {
    locationId: string;
    brandName: string;
    externalPayrollSystemId: string;
    boundTimesheetCount: number;
    sentTimesheetCount: number;
    customerSyncedAt: string | null;
    customerSyncError: string;
}

// Mirrors api/core/payroll_runs.go::PayrollRunError. The message is
// self-contained — worker / timesheet identity is baked into the message
// string at write time, so no read-time resolution is needed.
export interface PayrollRunErrorDto {
    id: string;
    payrollRunId: string;
    phase: number;
    entityType: number;
    entityId: string | null;
    message: string;
    createdAt: string;
}

// Fixed page size for the available browse list — mirrors
// api/core/payroll.go::PAYROLL_AVAILABLE_TIMESHEETS_PAGE_SIZE (server clamps
// anything larger).
export const AVAILABLE_PAGE_SIZE = 50;

interface AvailablePageResponse {
    items: TimesheetDto[];
    total: number;
    limit: number;
    offset: number;
}

export interface AvailableTimesheetFilter {
    branchId: string;
    deskId: string;
    workerId: string;
    from: string; // yyyy-MM-dd; empty = the agency's go-live floor
    approvedFrom: string; // yyyy-MM-dd; empty = no lower bound on approval date
    approvedTo: string; // yyyy-MM-dd (inclusive); empty = no upper bound
}

export function emptyAvailableTimesheetFilter(): AvailableTimesheetFilter {
    return { branchId: '', deskId: '', workerId: '', from: '', approvedFrom: '', approvedTo: '' };
}

async function fetchAvailablePage(runId: string, page: number, pageSize: number, filter: AvailableTimesheetFilter): Promise<AvailablePageResponse> {
    const offset = (page - 1) * pageSize;
    let query = `limit=${pageSize}&offset=${offset}`;
    if (filter.branchId) query += `&branchId=${filter.branchId}`;
    if (filter.deskId) query += `&deskId=${filter.deskId}`;
    if (filter.workerId) query += `&workerId=${filter.workerId}`;
    if (filter.from) query += `&from=${filter.from}`;
    if (filter.approvedFrom) query += `&approvedFrom=${filter.approvedFrom}`;
    if (filter.approvedTo) query += `&approvedTo=${filter.approvedTo}`;
    const response = await ajax.get(
        `/api/v1/agency/payroll-runs/${runId}/available-timesheets?${query}`);
    const data = response.data || {};
    return {
        items: data.items || [],
        total: data.total || 0,
        limit: data.limit || pageSize,
        offset: data.offset || offset,
    };
}

export interface IPayrollRunDetailsState {
    loading: boolean;
    saving: boolean;
    run: PayrollRunDto | null;
    timesheets: TimesheetDto[];
    availableTimesheets: TimesheetDto[];
    availableTotal: number;
    availablePage: number;
    workers: PayrollRunWorkerSummary[];
    locations: PayrollRunLocationSummary[];
    errors: PayrollRunErrorDto[];
    timesheetsLoaded: boolean;
    availableTimesheetsLoaded: boolean;
    workersLoaded: boolean;
    locationsLoaded: boolean;
    errorsLoaded: boolean;
    timesheetsLoading: boolean;
    availableTimesheetsLoading: boolean;
    workersLoading: boolean;
    locationsLoading: boolean;
    errorsLoading: boolean;
}

// Per-collection lazy-load policy:
//   - The DETAILS tab triggers `fetch(runId)` on mount (always needed).
//   - The TIMESHEETS / WORKERS / ERRORS tabs each call their matching
//     `ensureX(runId)` action when the tab is activated. ensureX is a no-op
//     when the data has already been loaded for this session.
//   - Mutation actions (add / remove / submit / ensureWorkers …) call
//     `invalidateX()` on every collection whose underlying data may have
//     changed, plus an immediate re-fetch of the collection the calling tab
//     is currently looking at. Other tabs reload lazily on their next view.
//
//   Tab badges that show the row count are intentionally NOT used here —
//   they'd force eager fetching of every collection just to display a count.
@Module({ dynamic: true, store, name: 'PayrollRunDetails', namespaced: true })
class PayrollRunDetails extends VuexModule implements IPayrollRunDetailsState {
    public loading = false;
    public saving = false;
    public run: PayrollRunDto | null = null;
    public timesheets: TimesheetDto[] = [];
    public availableTimesheets: TimesheetDto[] = [];
    // Paged browse list — mirrors the API's {items, total, limit, offset}
    // shape. Page size is fixed server-side at 50 (requests for more are
    // clamped); availablePage is 1-based for the v-data-table pager.
    public availableTotal = 0;
    public availablePage = 1;
    public availableFilter: AvailableTimesheetFilter = emptyAvailableTimesheetFilter();
    public workers: PayrollRunWorkerSummary[] = [];
    public locations: PayrollRunLocationSummary[] = [];
    public errors: PayrollRunErrorDto[] = [];
    public timesheetsLoaded = false;
    public availableTimesheetsLoaded = false;
    public workersLoaded = false;
    public locationsLoaded = false;
    public errorsLoaded = false;
    // Per-collection in-flight flags — every payroll grid binds its
    // loading visual to the matching flag so "empty" is never shown while
    // a fetch is still running (stale-while-revalidate keeps old rows on
    // screen during refreshes; these flags put a progress bar over them).
    public timesheetsLoading = false;
    public availableTimesheetsLoading = false;
    public workersLoading = false;
    public locationsLoading = false;
    public errorsLoading = false;

    @Mutation
    setLoading(v: boolean) { this.loading = v; }

    @Mutation
    setSaving(v: boolean) { this.saving = v; }

    @Mutation
    setRun(r: PayrollRunDto | null) { this.run = r; }

    @Mutation
    setTimesheets(items: TimesheetDto[]) {
        this.timesheets = items;
        this.timesheetsLoaded = true;
    }

    @Mutation
    setAvailableTimesheets(page: { items: TimesheetDto[]; total: number; page: number }) {
        this.availableTimesheets = page.items;
        this.availableTotal = page.total;
        this.availablePage = page.page;
        this.availableTimesheetsLoaded = true;
    }

    @Mutation
    setWorkers(items: PayrollRunWorkerSummary[]) {
        this.workers = items;
        this.workersLoaded = true;
    }

    @Mutation
    setLocations(items: PayrollRunLocationSummary[]) {
        this.locations = items;
        this.locationsLoaded = true;
    }

    @Mutation
    setErrors(items: PayrollRunErrorDto[]) {
        this.errors = items;
        this.errorsLoaded = true;
    }

    @Mutation
    setTimesheetsLoaded(v: boolean) { this.timesheetsLoaded = v; }

    @Mutation
    setAvailableTimesheetsLoaded(v: boolean) { this.availableTimesheetsLoaded = v; }

    @Mutation
    setWorkersLoaded(v: boolean) { this.workersLoaded = v; }

    @Mutation
    setLocationsLoaded(v: boolean) { this.locationsLoaded = v; }

    @Mutation
    setErrorsLoaded(v: boolean) { this.errorsLoaded = v; }

    @Mutation
    setTimesheetsLoading(v: boolean) { this.timesheetsLoading = v; }

    @Mutation
    setAvailableTimesheetsLoading(v: boolean) { this.availableTimesheetsLoading = v; }

    @Mutation
    setWorkersLoading(v: boolean) { this.workersLoading = v; }

    @Mutation
    setLocationsLoading(v: boolean) { this.locationsLoading = v; }

    @Mutation
    setErrorsLoading(v: boolean) { this.errorsLoading = v; }

    @Action
    async fetch(id: string) {
        this.setLoading(true);
        try {
            const response = await ajax.get(`/api/v1/agency/payroll-runs/${id}`);
            this.setRun(response.data);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setRun(null);
        } finally {
            this.setLoading(false);
        }
    }

    // Force-fetch actions — always hit the API. Used after a mutation.
    @Action
    async fetchTimesheets(id: string) {
        this.setTimesheetsLoading(true);
        try {
            const response = await ajax.get(`/api/v1/agency/payroll-runs/${id}/timesheets`);
            this.setTimesheets(response.data || []);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setTimesheets([]);
        } finally {
            this.setTimesheetsLoading(false);
        }
    }

    @Mutation
    setAvailableFilter(filter: AvailableTimesheetFilter) {
        this.availableFilter = filter;
    }

    // Applies a new browse filter and refetches from page 1.
    @Action
    async applyAvailableFilter(payload: { runId: string; filter: AvailableTimesheetFilter }) {
        this.setAvailableFilter(payload.filter);
        await this.fetchAvailableTimesheets({ runId: payload.runId, page: 1 });
    }

    // Fetches one page of the available pool (server pages at 50). Accepts a
    // 1-based page; defaults to the page currently in view so mutation
    // actions can refresh in place. If the requested page has emptied out
    // (rows were bound/removed), steps back to the last non-empty page.
    @Action
    async fetchAvailableTimesheets(payload: { runId: string; page?: number }) {
        const pageSize = AVAILABLE_PAGE_SIZE;
        let page = payload.page || this.availablePage || 1;
        this.setAvailableTimesheetsLoading(true);
        try {
            let data = await fetchAvailablePage(payload.runId, page, pageSize, this.availableFilter);
            if (data.items.length === 0 && data.total > 0 && page > 1) {
                page = Math.max(1, Math.ceil(data.total / pageSize));
                data = await fetchAvailablePage(payload.runId, page, pageSize, this.availableFilter);
            }
            this.setAvailableTimesheets({ items: data.items, total: data.total, page });
        } catch (error) {
            AppModule.handleApiError(error);
            this.setAvailableTimesheets({ items: [], total: 0, page: 1 });
        } finally {
            this.setAvailableTimesheetsLoading(false);
        }
    }

    // Server-side sweep of the ENTIRE available pool onto the run — behind
    // the explicit "Add all" button (the header checkbox only selects the
    // visible page). Atomic on the server: no shipping hundreds of IDs.
    @Action
    async addAllAvailable(runId: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${runId}/timesheets/add-all-available`, {});
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await Promise.all([
                this.fetchTimesheets(runId),
                this.fetchAvailableTimesheets({ runId, page: 1 }),
            ]);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    @Action
    async fetchWorkers(id: string) {
        this.setWorkersLoading(true);
        try {
            const response = await ajax.get(`/api/v1/agency/payroll-runs/${id}/workers`);
            this.setWorkers(response.data || []);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setWorkers([]);
        } finally {
            this.setWorkersLoading(false);
        }
    }

    @Action
    async fetchLocations(id: string) {
        this.setLocationsLoading(true);
        try {
            const response = await ajax.get(`/api/v1/agency/payroll-runs/${id}/locations`);
            this.setLocations(response.data || []);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setLocations([]);
        } finally {
            this.setLocationsLoading(false);
        }
    }

    @Action
    async fetchErrors(id: string) {
        this.setErrorsLoading(true);
        try {
            const response = await ajax.get(`/api/v1/agency/payroll-runs/${id}/errors`);
            this.setErrors(response.data || []);
        } catch (error) {
            AppModule.handleApiError(error);
            this.setErrors([]);
        } finally {
            this.setErrorsLoading(false);
        }
    }

    // Lazy ensure-loaded actions — no-op if the data was loaded earlier and
    // hasn't been invalidated. Called by each tab on activation. Named with
    // the "Loaded" suffix to keep them distinct from the existing
    // `ensureWorkers` mutation action below (which pushes workers to payroll).
    @Action
    async ensureTimesheetsLoaded(id: string) {
        if (this.timesheetsLoaded) return;
        await this.fetchTimesheets(id);
    }

    @Action
    async ensureAvailableTimesheetsLoaded(id: string) {
        if (this.availableTimesheetsLoaded) return;
        await this.fetchAvailableTimesheets({ runId: id, page: 1 });
    }

    @Action
    async ensureWorkersLoaded(id: string) {
        if (this.workersLoaded) return;
        await this.fetchWorkers(id);
    }

    @Action
    async ensureLocationsLoaded(id: string) {
        if (this.locationsLoaded) return;
        await this.fetchLocations(id);
    }

    @Action
    async ensureErrorsLoaded(id: string) {
        if (this.errorsLoaded) return;
        await this.fetchErrors(id);
    }

    // Invalidate the loaded flag so the next tab activation triggers a fetch.
    // Existing data stays in place so the UI doesn't flash empty if the user
    // is currently on that tab — they get stale-while-revalidate.
    @Action invalidateTimesheets() { this.setTimesheetsLoaded(false); }
    @Action invalidateAvailableTimesheets() { this.setAvailableTimesheetsLoaded(false); }
    @Action invalidateWorkers() { this.setWorkersLoaded(false); }
    @Action invalidateLocations() { this.setLocationsLoaded(false); }
    @Action invalidateErrors() { this.setErrorsLoaded(false); }

    // Mutation actions — invalidate every collection whose backing data may
    // have changed. The caller (the tab driving the action) is responsible
    // for refreshing what it's currently showing.

    // The three send steps run as background tasks (202 on enqueue) under
    // the run's single task lock — a refusal while another task is running
    // surfaces the backend's wait message via handleApiError. The refetches
    // below pick up the lock/task fields on the run (shown on the PurePro
    // tab); the collections are invalidated so revisiting a tab after the
    // task completes pulls the results.

    @Action
    async ensureWorkers(id: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${id}/ensure-workers`, {});
            this.setWorkersLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    @Action
    async ensureCustomers(id: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${id}/ensure-customers`, {});
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    @Action
    async submitTimesheets(id: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${id}/submit-timesheets`, {});
            this.setTimesheetsLoaded(false);
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    // Mistake-send recovery from the Workers tab: clears the sent marker
    // and unbinds the selected timesheets for one worker (they must have
    // been deleted in the payroll system by hand first), dropping the
    // worker from the run if nothing of theirs remains bound.
    @Action
    async rollbackWorkerSentTimesheets(payload: { runId: string; workerId: string; timesheetIds: string[] }) {
        this.setSaving(true);
        try {
            await ajax.post(
                `/api/v1/agency/payroll-runs/${payload.runId}/workers/${payload.workerId}/rollback-sent-timesheets`,
                { timesheetIds: payload.timesheetIds });
            // Timesheets leave the run (and reappear in the available
            // pool); locations may drop off; counters on the run change.
            this.setTimesheetsLoaded(false);
            this.setAvailableTimesheetsLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await Promise.all([this.fetch(payload.runId), this.fetchWorkers(payload.runId)]);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    // Recovery for the "sent but the response said failure" case: records
    // one timesheet as sent WITHOUT calling the payroll system. The backend
    // stamps the marker exactly as a successful submission would and
    // refuses once the run is Syncing Data or beyond.
    @Action
    async markTimesheetSent(payload: { runId: string; timesheetId: string }) {
        this.setSaving(true);
        try {
            await ajax.post(
                `/api/v1/agency/payroll-runs/${payload.runId}/timesheets/${payload.timesheetId}/mark-sent`,
                {});
            // The run's pushed counter changes (and a Submitting run can
            // advance to Submitted); the timesheet's chip flips to Sent.
            this.setTimesheetsLoaded(false);
            await Promise.all([this.fetch(payload.runId), this.fetchTimesheets(payload.runId)]);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    // Manual status change from the run's Set Status menu. The backend
    // enforces the legal transitions and their gates (provider control
    // jobs for reopen / Syncing Data; everything-sent for Submitted) —
    // a refusal surfaces as the backend's explanation via handleApiError.
    @Action
    async updateStatus(payload: { id: string; status: number }) {
        this.setSaving(true);
        try {
            await ajax.put(`/api/v1/agency/payroll-runs/${payload.id}/status`, {
                updatedAt: this.run ? this.run.updatedAt : null,
                status: payload.status,
            });
            // A status change shifts what every tab shows (reopen unlocks
            // the browse list; Syncing Data unlocks the fetch steps) —
            // invalidate broadly and refetch the run.
            this.setAvailableTimesheetsLoaded(false);
            this.setTimesheetsLoaded(false);
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(payload.id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    // Manual "Fetch payslips" step — enqueues the async task (Cloud Task;
    // it can take a while). Idempotent server-side.
    @Action
    async fetchPayslipsStep(id: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${id}/fetch-payslips`, {});
            this.setWorkersLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    // Manual "Fetch holiday" step — independent of the payslip step.
    @Action
    async fetchHolidayStep(id: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${id}/fetch-holiday`, {});
            this.setWorkersLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    // FAILED → re-enqueue the pull task.
    @Action
    async retryPull(id: string) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${id}/retry-pull`, {});
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await this.fetch(id);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    @Action
    async addTimesheets(payload: { runId: string; timesheetIds: string[] }) {
        this.setSaving(true);
        try {
            await ajax.post(`/api/v1/agency/payroll-runs/${payload.runId}/timesheets`, { timesheetIds: payload.timesheetIds });
            // Worker / location sets may have changed (new entities brought
            // in by their first bound timesheet); errors may have new rows;
            // available set shrinks. Re-fetch the two collections the
            // timesheets tab is looking at right now.
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await Promise.all([this.fetchTimesheets(payload.runId), this.fetchAvailableTimesheets({ runId: payload.runId })]);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.setSaving(false);
        }
    }

    @Action
    async unaddTimesheets(payload: { runId: string; timesheetIds: string[] }) {
        this.setSaving(true);
        try {
            // DELETE with a JSON body — same convention as removing
            // timesheets from an invoice (see invoiceDetails.ts).
            await ajax.delete(`/api/v1/agency/payroll-runs/${payload.runId}/timesheets`, {
                data: { timesheetIds: payload.timesheetIds },
            });
            // Same invalidation as add — removing may drop a worker or
            // location from the run if it was the last bound timesheet.
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            this.setErrorsLoaded(false);
            await Promise.all([this.fetchTimesheets(payload.runId), this.fetchAvailableTimesheets({ runId: payload.runId })]);
        } catch (error) {
            AppModule.handleApiError(error);
            // A partial removal (some rows already pushed) comes back as an
            // error AFTER the removable rows were unbound — refresh so the
            // lists reflect what actually happened.
            this.setWorkersLoaded(false);
            this.setLocationsLoaded(false);
            await Promise.all([this.fetchTimesheets(payload.runId), this.fetchAvailableTimesheets({ runId: payload.runId })]);
        } finally {
            this.setSaving(false);
        }
    }

    @Action
    cleanState() {
        this.context.commit('resetState');
    }

    @Mutation
    resetState() {
        this.loading = false;
        this.saving = false;
        this.run = null;
        this.timesheets = [];
        this.availableTimesheets = [];
        this.availableTotal = 0;
        this.availablePage = 1;
        this.workers = [];
        this.locations = [];
        this.errors = [];
        this.timesheetsLoaded = false;
        this.availableTimesheetsLoaded = false;
        this.workersLoaded = false;
        this.locationsLoaded = false;
        this.errorsLoaded = false;
    }
}

export const PayrollRunDetailsModule = getModule(PayrollRunDetails);
